/* Walking a keyset listing, once, for every screen that pages one. S-78.
 *
 * **Extracted rather than copied, and the reason is that "what does Back mean" is a decision.** The
 * audit screen worked it out first and held it inline; `CatalogBrowser` is the second screen to need
 * it, and a second implementation would be two answers to the same question — the two-vocabularies
 * defect this codebase records at five sites, in the one place where being subtly wrong is invisible
 * (a page that quietly repeats or skips a row looks like a page).
 *
 * Three properties, and each one is a fact about keyset paging rather than a UI preference.
 *
 * **Back needs a stack**, because keyset paging is forward-only by construction: a cursor names where
 * to *resume*, not where to return to. So going back means remembering where each page started, and
 * the stack begins with `null` — the first page's cursor — rather than empty, so Back from page 2 has
 * somewhere to go without a special case.
 *
 * **A filter change resets the stack rather than reusing a cursor.** The token is signed over the
 * tenant *and* a fingerprint of the query, so carrying one across a filter change is refused with a
 * 400. Resetting says the same thing without the round trip, and it is the honest answer: a position
 * in one result set means nothing in another.
 *
 * **There is a page number and never a total.** A keyset listing cannot know how many pages exist
 * without counting the whole table, and inventing a denominator is worse than omitting one — it is
 * the `next_cursor: null`-while-truncating defect wearing a fraction.
 */

import { useCallback, useState } from 'react';

/** The first page's cursor. `null`, and present in the stack rather than implied.
 *
 * Exported so a caller can compare against it — a screen that wants to know "am I on page 1" should
 * ask the hook rather than test `stack.length`, which is the kind of arithmetic that drifts.
 */
const FIRST_PAGE: readonly (string | null)[] = [null];

export interface CursorStack {
  /** The cursor to request. `null` on the first page. */
  readonly cursor: string | null;
  /** 1-based, for display. Never accompanied by a total — see the module comment. */
  readonly page: number;
  /** Whether Back is possible. False on page 1. */
  readonly canGoBack: boolean;
  /** Steps back one page. A no-op on page 1, so a caller need not guard it as well as disable it. */
  readonly back: () => void;
  /** Steps forward, given the cursor the current page returned.
   *
   * Takes the token rather than reading it from a query result, so this module needs to know nothing
   * about the fetching layer — and `null` or `undefined` is a no-op, which means a caller cannot
   * advance past the last page even if the button's disabled state is wrong.
   */
  readonly forward: (next: string | null | undefined) => void;
  /** Returns to page 1. **Call this on every filter change**, for the reason above. */
  readonly reset: () => void;
}

export function useCursorStack(): CursorStack {
  const [stack, setStack] = useState<readonly (string | null)[]>(FIRST_PAGE);

  const back = useCallback(() => {
    setStack((current) => (current.length <= 1 ? current : current.slice(0, -1)));
  }, []);

  const forward = useCallback((next: string | null | undefined) => {
    if (next == null) return;
    setStack((current) => [...current, next]);
  }, []);

  const reset = useCallback(() => {
    setStack(FIRST_PAGE);
  }, []);

  return {
    cursor: stack[stack.length - 1] ?? null,
    page: stack.length,
    canGoBack: stack.length > 1,
    back,
    forward,
    reset,
  };
}
